<script setup lang="ts">
import type { DropdownOption } from 'naive-ui'
import { Icon } from '#components'
import * as Sentry from '@sentry/nuxt'

defineProps({
  collapsed: {
    type: Boolean,
    default: false,
  },
})

const { user } = useUserStore()

type AccountDropDownKey = 'colormode' | 'report' | 'logout' | 'settings'

const { colorModePreference } = useNaiveColorMode()
const colorModeNuxt = useColorMode()

const dropDownOptions: Array<DropdownOption & { key: AccountDropDownKey }> = [
  {
    label: () =>
      colorModeNuxt.value === 'dark'
        ? 'Dunkel'
        : 'Hell',
    key: 'colormode',
    icon: () =>
      colorModeNuxt.value === 'dark'
        ? h(Icon, { name: 'i-ic-outline-dark-mode' })
        : h(Icon, { name: 'i-ic-outline-light-mode' }),
  },
  {
    label: 'Fehler melden',
    key: 'report',
    icon: () => h(Icon, { name: 'i-fa6-solid-bullhorn' }),
  },
  {
    label: 'Einstellungen',
    key: 'settings',
    icon: () => h(Icon, { name: 'i-ic-baseline-settings' }),
  },
  {
    label: 'Abmelden',
    key: 'logout',
    icon: () => h(Icon, { name: 'i-ic-baseline-logout' }),
  },
]

const { logout } = useAuthStore()

function toggleColorMode() {
  if (colorModeNuxt.value === 'dark') {
    colorModeNuxt.preference = 'light'
    return
  }
  colorModeNuxt.preference = 'dark'
}

watch(() => colorModeNuxt.value, (newValue) => {
  if (newValue === 'dark') {
    colorModePreference.set('dark')
    return
  }
  colorModePreference.set('light')
}, { immediate: true })

function accountDropdownSelect(key: AccountDropDownKey) {
  switch (key) {
    case 'logout':
      logout()
      break
    case 'settings':
      navigateTo({ name: 'settings' })
      break
    case 'colormode':
      toggleColorMode()
      break
    case 'report':
      openSentry()
      break
  }
}

async function openSentry() {
  const feedback = Sentry.feedbackIntegration({ autoInject: false })
  const form = await feedback.createForm()
  form.appendToDom() // Fügt das Formular zum DOM hinzu
  form.open() // Öffnet das Formular
}
</script>

<template>
  <aside
    class="my-4 w-full grid items-center pl-6.5 pr-2 grid-cols-[36px_1fr_36px] items-center"
    :class="{ '!px-4': collapsed }"
  >
    <NDropdown
      trigger="click"
      :options="dropDownOptions"
      @select="accountDropdownSelect"
    >
      <NButton circle quaternary>
        <template #icon>
          <Icon name="i-ic-outline-account-circle" />
        </template>
      </NButton>
    </NDropdown>
    <div v-show="!collapsed" class="grow">
      {{ user?.forename }} {{ user?.surname }}
    </div>
    <NDropdown
      trigger="click"
      :options="dropDownOptions"
      @select="accountDropdownSelect"
    >
      <NButton
        v-show="!collapsed"
        circle
        quaternary
      >
        <template #icon>
          <Icon name="i-ic-outline-settings" />
        </template>
      </NButton>
    </NDropdown>
  </aside>
</template>
